import { themeVar } from '@/styles/theme';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { DueDiligencePageGetDueDiligenceRecordQuery, DueDiligenceRecordStatus, DueDiligenceRecordTierStatus } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { BurgerMenuIcon } from '@/assets/static/icons/burger-menu';
import { CloseMenuIcon } from '@/assets/static/icons/close-menu';
import { AiAssistant, aiPaneTabsOnEditFieldPages } from '@/components/aiAssistant';
import { GET_AI_ASSET_KEY_QUERY } from '@/components/aiAssistant/queries';
import { IAiAssets, IAiRunPromptAnswerCitation } from '@/components/aiAssistant/types';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { TabsTitle } from '@/components/basicComponents/tabs';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import { FieldEditorModalPage } from '@/features/fieldEditorModalPages/FieldEditorModalPage';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useGoBack } from '@/hooks/useGoBack';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { convertDueDiligenceFieldToFieldEditor, FieldList, hasAiAnswer } from '@/shared/ui/fieldList';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { extractAndFormatDate } from '@/shared/utils/fieldEditor/extractAndFormatDate';
import { extractAndFormatDollar } from '@/shared/utils/fieldEditor/extractAndFormatDollar';
import { extractAndFormatNumber } from '@/shared/utils/fieldEditor/extractAndFormatNumber';
import { extractAndFormatPercentage } from '@/shared/utils/fieldEditor/extractAndFormatPercentage';
import { AIFieldType } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useResponsive } from '../../../hooks/use-responsive';
import { MainWrap, PaddingWrap } from '../../../styles/common';
import { Loader } from '../../loaders/loader';
import MobileModal from '../../mobileModal';
import {
  ADD_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION,
  DELETE_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION,
  DUE_DILIGENCE_RECORD_QUERY,
  GET_LATEST_DUE_DILIGENCE_FUNNEL_QUERY,
  GET_URL_FOR_UPLOAD,
  UPDATE_FIELD_ANSWER_MUTATION,
  UPDATE_RECORD_STATUS_MUTATION,
  UPDATE_TIER_STATUS_MUTATION
} from '../queries';
// import { DataRoom } from './data-room';
import FieldModal from './fieldModal';
import { MobileNotes } from './mobileNotes';
import Notes from './notes';
import { TierCheckList } from './tierCheckList';
import { TierDropdown } from './tierDropdown';
import TierStatus from './tierStatus';
import {
  DueDiligenceRecordTier,
  DueDiligenceRecordTierChecklistItem,
  DueDiligenceRecordTierSection,
  DueDiligenceRecordTierSectionField,
  ITier,
  NoteDataType
} from './types';

const getDataForUpdate = (row: IFieldEditor): { value: string; commentary: string } => {
  switch (row.documentFieldType) {
    case getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT):
      return {
        value: row.AI.answers[0],
        commentary: null
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.NUMBER):
      return {
        value: extractAndFormatNumber(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DOLLAR):
      return {
        value: extractAndFormatDollar(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.PERCENTAGE):
      return {
        value: extractAndFormatPercentage(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.DATE):
      return {
        value: extractAndFormatDate(row.AI.answers[0]),
        commentary: row.AI.answers[0]
      };
    case getEnumKeyByValue(AIFieldType, AIFieldType.JSON):
      return {
        value: row.AI?.answerJson,
        commentary: null
      };
    default:
      return {
        value: '',
        commentary: ''
      };
  }
};

export interface IFile extends File {
  investmentId?: string;
  loading?: boolean;
  status?: string;
}

export const DueDiligenceRecord = () => {
  const { isMobile, isTablet } = useResponsive();
  const params = useParams();
  const location = useLocation();
  const goBack = useGoBack();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';
  const isFromDocx = searchParams.get('isFromDocx') === 'true';
  const openAiPaneSearchParam = searchParams.get('openAiPane') === 'true';
  const editFieldPageType = searchParams.get('editFieldPageType') || 'default';
  const fieldId = searchParams.get('fieldId') || '';

  const [activeTab, setActiveTab] = useState('');
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [noteData, setNoteData] = useState<NoteDataType | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [currentRow, setCurrentRow] = useState<
    DueDiligenceRecordTier | DueDiligenceRecordTierSection | DueDiligenceRecordTierSectionField | DueDiligenceRecordTierChecklistItem | null
  >(null);
  const [tiers, setTiers] = useState<DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers']>([]);
  const [recordStatus, setRecordStatusStatus] = useState<DueDiligenceRecordStatus | null>(null);
  const [isUpdateActiveTab, setIsUpdateActiveTab] = useState(true);
  const [isBackToLoading, setIsBackToLoading] = useState(false);
  const [scrollTo, setScrollTo] = useState(0);
  const [isAiAssistantOpen, setIsAiAssistantOpen] = useState(false);
  const [filesForAiAssistant, setFilesForAiAssistant] = useState<IAiAssets[]>([]);
  const [citationsTabData, setCitationsTabData] = useState<
    {
      id: string;
      fieldName: string;
      citations: IAiRunPromptAnswerCitation[];
    }[]
  >([]);
  const [isDataRoomOpen, setIsDataRoomOpen] = useState(false);
  const [generateAnswersLoading, setGenerateAnswersLoading] = useState(false);
  const [isGenerateAnswersLoadingCompleted, setIsGenerateAnswersLoadingCompleted] = useState(false);
  const [currentField, setCurrentField] = useState<IFieldEditor | null>(null);
  const [allFields, setAllFields] = useState<IFieldEditor[] | null>(null);
  const [showTabs, setShowTabs] = useState(true);
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);
  const [recordName, setRecordName] = useState('');

  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const burgerMenuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside<HTMLDivElement>(burgerMenuRef, () => setIsTabMenuOpen(false));

  const [getAiAssetKey] = useLazyQuery(GET_AI_ASSET_KEY_QUERY);
  const [getAssetSignedUrl] = useLazyQuery(GET_ASSET_SIGNED_URL_QUERY);

  const {
    data: dueDiligenceRecordData,
    loading: dueDiligenceRecordLoading,
    error: dueDiligenceRecordError,
    refetch: dueDiligenceRecordRefetch
  } = useQuery(DUE_DILIGENCE_RECORD_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: params?.recordId
    }
  });

  useEffect(() => {
    if (dueDiligenceRecordData) {
      const fetchData = async () => {
        const { investment, funnel } = dueDiligenceRecordData?.getDueDiligenceRecord || {};

        setRecordName(investment?.name || '');

        const { data: funnelData } = await client.query({
          fetchPolicy: 'network-only',
          query: GET_LATEST_DUE_DILIGENCE_FUNNEL_QUERY,
          variables: {
            id: funnel?.id
          }
        });

        const updatedTiers = funnel?.tiers?.map((oldTier) => {
          const matchingTier = funnelData.getDueDiligenceFunnel.latestFunnel.tiers.find((tier) => tier.order === oldTier.order);

          if (matchingTier) {
            return {
              ...oldTier,
              name: matchingTier.name
            };
          }
          return oldTier;
        });

        setTiers(updatedTiers);

        const findActiveTier = updatedTiers.find((tier) => {
          if (dueDiligenceRecordData?.getDueDiligenceRecord?.activeTier?.order === tier.order) {
            return tier;
          }
        });

        if (findActiveTier && isUpdateActiveTab) {
          setActiveTab(findActiveTier.name);
        }
      };

      fetchData();
    }
  }, [dueDiligenceRecordData, isUpdateActiveTab, client]);

  useEffect(() => {
    if (!dueDiligenceRecordError && !dueDiligenceRecordLoading) {
      const { id, status, funnel } = dueDiligenceRecordData?.getDueDiligenceRecord || {};
      const fields: DueDiligencePageGetDueDiligenceRecordQuery['getDueDiligenceRecord']['funnel']['tiers'][number]['sections'][number]['fields'] = [];

      funnel?.tiers?.forEach((tier) => {
        tier.sections.forEach((section) => {
          fields.push(...section.fields);
        });
      });

      const allEditFields: IFieldEditor[] = convertDueDiligenceFieldToFieldEditor(fields, id);
      setAllFields(allEditFields);

      if (isUpdateActiveTab) {
        // setActiveTab(data.getDueDiligenceRecord.activeTier.name);
        setIsUpdateActiveTab(false);
      }
      setRecordStatusStatus(status || null);
    }
  }, [dueDiligenceRecordData, dueDiligenceRecordLoading, dueDiligenceRecordError]);

  useEffect(() => {
    if (fieldId && !allFields?.length) return;
    const currentField = allFields?.find((field) => field.id === fieldId);

    if (currentField) {
      setCurrentField(currentField);
      handleChangeEditFieldPageType(currentField?.documentFieldType || null);
    }
  }, [fieldId, allFields]);

  useEffect(() => {
    if (editFieldPageType === 'default') {
      window.scrollTo(0, scrollTo);
    }
  }, [editFieldPageType]);

  useEffect(() => {
    const { openAiPane } = location.state || {};

    if (openAiPane) {
      setIsAiAssistantOpen(true);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state]);

  useEffect(() => {
    if (openAiPaneSearchParam) {
      setIsAiAssistantOpen(true);
      setSearchParams(
        (prev) => {
          prev.delete('openAiPane');
          return prev;
        },
        { replace: true }
      );
    }
  }, [openAiPaneSearchParam]);

  useEffect(() => {
    if (!isFromDocx) return;

    if (currentField) {
      (async () => {
        try {
          const filename = searchParams.get('filename') || '';
          const pageNumber = searchParams.get('pageNumber') || '';

          const { data: getDealAssistentAIAssetKeyData } = await getAiAssetKey({
            fetchPolicy: 'network-only',
            variables: {
              data: {
                recordId: params?.recordId,
                filename: decodeURIComponent(filename)
              }
            }
          });

          const { data: getAssetSignedUrlData } = await getAssetSignedUrl({
            fetchPolicy: 'network-only',
            variables: {
              assetKey: getDealAssistentAIAssetKeyData?.getDealAssistentAIAssetKey
            }
          });
          window.open(`${getAssetSignedUrlData.getAssetSignedUrl}#page=${pageNumber}`, '_blank');
        } catch (error) {
          console.log(error);
        }
      })();

      setIsAiAssistantOpen(true);
      setSearchParams(
        (prev) => {
          prev.delete('isFromDocx');
          prev.delete('filename');
          prev.delete('pageNumber');
          return prev;
        },
        { replace: true }
      );
    }
  }, [isFromDocx, currentField]);

  useEffect(() => {
    if (editFieldPageType !== 'default') {
      setCitationsTabData([{ id: currentField?.id, fieldName: currentField?.name, citations: currentField?.AI?.citations ?? [] }]);
    }
  }, [currentField, editFieldPageType]);

  useEffect(() => {
    const checkScreenWidth = () => {
      const tabsContainer = tabsContainerRef.current;

      if (tabsContainer) {
        const tabElements = Array.from(tabsContainer.children) as HTMLDivElement[];

        let totalTabWidth = 0;
        tabElements.forEach((tabElement) => {
          totalTabWidth += tabElement.offsetWidth;
        });

        const screenWidth = window.innerWidth;

        setShowTabs(totalTabWidth <= screenWidth - 200);
      }
    };

    if (tabsContainerRef.current) {
      checkScreenWidth();
    }

    window.addEventListener('resize', checkScreenWidth);

    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, [tiers, tabsContainerRef]);

  const [addAttachment] = useMutation(ADD_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION);
  const [deleteAttachment] = useMutation(DELETE_DUE_DILIGENCE_RECORD_ATTACHMENT_MUTATION);
  const [updateFieldAnswer] = useMutation(UPDATE_FIELD_ANSWER_MUTATION);
  const [updateTierStatus] = useMutation(UPDATE_TIER_STATUS_MUTATION, {
    onCompleted: () => {
      setIsUpdateActiveTab(false);
      dueDiligenceRecordRefetch();
    }
  });
  const [updateRecordStatus] = useMutation(UPDATE_RECORD_STATUS_MUTATION, {
    onCompleted: () => {
      setIsUpdateActiveTab(false);
      dueDiligenceRecordRefetch();
    }
  });

  const openNotes = (
    note: string,
    updateAt: Date,
    task: string,
    assignedBy?: DueDiligenceRecordTierSection['data']['assignedBy'] | DueDiligenceRecordTierChecklistItem['data']['assignedBy'],
    assignedTo?: DueDiligenceRecordTierSection['data']['assignedTo'] | DueDiligenceRecordTierChecklistItem['data']['assignedTo']
  ) => {
    setIsNotesOpen(!isNotesOpen);
    setNoteData({ updateAt, note, task, assignedBy, assignedTo });
  };

  const openModalWindow = (
    type: string,
    row: DueDiligenceRecordTier | DueDiligenceRecordTierSection | DueDiligenceRecordTierSectionField | DueDiligenceRecordTierChecklistItem,
    fieldEditor?: IFieldEditor
  ) => {
    if (row === null && fieldEditor) {
      setModalWindow({ isOpen: true, type });
      setCurrentField(fieldEditor);
      return;
    }

    setModalWindow({ isOpen: true, type });
    setCurrentRow(row);
  };

  const closeModalWindow = () => {
    setModalWindow({ type: '', isOpen: false });
    setCurrentField(null);
  };

  const handleChangeFieldId = (fieldId: string | null) => {
    setSearchParams(
      (prev) => {
        if (!fieldId) {
          prev.delete('fieldId');
          return prev;
        }
        prev.set('fieldId', fieldId);
        return prev;
      },
      { replace: true }
    );
  };

  const handleChangeEditFieldPageType = (editFieldPageType: string | null) => {
    setSearchParams(
      (prev) => {
        if (!editFieldPageType) {
          prev.delete('editFieldPageType');
          return prev;
        }
        prev.set('editFieldPageType', editFieldPageType);
        return prev;
      },
      { replace: true }
    );
  };

  const onEditField = (id: string, documentFieldType: string) => {
    setScrollTo(window.scrollY);
    handleChangeEditFieldPageType(documentFieldType);
    const editField = allFields?.find((field) => field.id === id);
    handleChangeFieldId(id);
    setCurrentField(editField || null);
    dueDiligenceRecordRefetch();
  };

  const closeEditPage = () => {
    handleChangeEditFieldPageType(null);
    handleChangeFieldId(null);
    setCurrentField(null);
    dueDiligenceRecordRefetch();
  };

  const backToPreviousTier = (tierData: ITier) => {
    setIsBackToLoading(true);
    setIsUpdateActiveTab(true);
    updateTierStatus({
      variables: {
        data: {
          id: tierData.data.id,
          status: DueDiligenceRecordTierStatus.NotStarted
        }
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: DUE_DILIGENCE_RECORD_QUERY,
          variables: {
            id: params?.recordId
          }
        }
      ],
      onCompleted: () => {
        setIsBackToLoading(false);
      }
    });
  };

  const advanceToNexTier = () => {
    setIsUpdateActiveTab(true);
    window.scrollTo(0, 0);
  };

  const isLastTier = () => tiers[tiers.length - 1].name === activeTab;

  const isFieldValue = () => {
    const currentTier = tiers.filter((tier) => tier.name === activeTab && tier);
    const isSectionCompleted = currentTier[0]?.sections?.filter((item) => !item.data.completed);
    const isCheckListCompleted = currentTier[0]?.checklistItems?.filter((item) => !item.data.completed && item.required);
    return Boolean(isSectionCompleted?.length || isCheckListCompleted?.length);
  };

  const prevTierStatus = () => {
    if (activeTab === tiers[0].name) return null;
    const tierNumber = Number(activeTab[activeTab.length - 1]);
    const prevTier = tiers.filter((tier) => tier.name === `Tier ${tierNumber - 1}` && tier);
    return prevTier[0]?.data.status;
  };

  const totalScore = () => {
    const currentTier = tiers.filter((tier) => tier.name === activeTab && tier);
    return currentTier[0].sections.reduce((accumulator, currentValue) => accumulator + currentValue.data.score, 0);
  };

  const handleGenerateAnswersLoadingChange = (loadingStatus: boolean, showCompleteIcon: boolean) => {
    setGenerateAnswersLoading(loadingStatus);
    setIsGenerateAnswersLoadingCompleted(showCompleteIcon);
  };

  const openAdvancedOptions = () => {
    setIsAiAssistantOpen(false);
    handleChangeEditFieldPageType('advanced-options');
  };

  const closeAdvancedOptions = () => {
    setIsAiAssistantOpen(true);
    handleChangeEditFieldPageType(null);
  };

  const changeActiveFunnel = (tab: string) => {
    setActiveTab(tab);
    setIsTabMenuOpen(false);
  };

  const handleFilesForAiAssistant = (files: IAiAssets[]) => {
    setFilesForAiAssistant(files);
  };

  const handleUpdateFieldAnswer = async ({
    id,
    recordId,
    value,
    commentary
  }: {
    id: string;
    recordId: string;
    value: string;
    commentary?: string;
  }) => {
    await updateFieldAnswer({
      variables: {
        data: {
          id,
          value,
          commentary
        }
      }
    });
  };

  const handleDeleteAttachment = async ({ id }: { id: string }) => {
    await deleteAttachment({
      variables: {
        id
      }
    });
  };

  const handleUploadAttachment = async ({
    file,
    recordId,
    fieldId,
    fieldAnswerId
  }: {
    file: File;
    recordId: string;
    fieldId: string;
    fieldAnswerId: string;
  }) => {
    const { data } = await client.query({
      query: GET_URL_FOR_UPLOAD,
      variables: {
        data: {
          contentType: file.type,
          recordId: recordId,
          fieldId: fieldAnswerId,
          filename: file.name.replace(/\s/g, '')
        }
      }
    });

    await fetch(data.getDueDiligenceRecordFieldAnswerAttachmentUploadUrl.url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    const { data: addAttachmentData } = await addAttachment({
      variables: {
        data: {
          id: fieldAnswerId,
          assetKey: data.getDueDiligenceRecordFieldAnswerAttachmentUploadUrl.assetKey,
          includeInAutomatedContent: true
        }
      }
    });

    const { id, asset } = addAttachmentData?.addDueDiligenceRecordFieldAnswerAttachment || {};

    return { id, url: asset?.url || '', assetKey: asset?.assetKey || '' } as { id: string; url: string; assetKey: string };
  };

  const onAcceptAiAnswer = async (field: IFieldEditor) => {
    const { value, commentary } = getDataForUpdate(field);

    await updateFieldAnswer({
      variables: {
        data: {
          id: field.fieldAnswerId,
          value,
          commentary
        }
      }
    });

    await dueDiligenceRecordRefetch();
  };

  const onClearField = async (field: IFieldEditor) => {
    const commentary =
      hasAiAnswer(field) &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT) &&
      field.documentFieldType !== getEnumKeyByValue(AIFieldType, AIFieldType.SHORT_TEXT)
        ? ''
        : null;

    await updateFieldAnswer({
      variables: {
        data: {
          id: field.fieldAnswerId,
          value: field?.AI?.answers?.length ? '' : null,
          commentary
        }
      }
    });

    await dueDiligenceRecordRefetch();
  };

  if (dueDiligenceRecordLoading || !tiers.length) {
    return <CustomLoader size={60} />;
  }

  return isDataRoomOpen ? (
    <></>
  ) : (
    // <DataRoom setIsDataRoomOpen={setIsDataRoomOpen} />
    <>
      <DueDiligenceFunnelWrapper>
        {((!isMobile && !isTablet) || !isAiAssistantOpen) && (
          <>
            {/* {editFieldPage.typeWindow === 'advanced-options' && (
              <AdvancedOptions
                handleClose={closeAdvancedOptions}
                dueDiligenceRecordsRefetch={refetch}
                files={files}
                setFiles={setFiles}
                handleGenerateAnswersLoadingChange={handleGenerateAnswersLoadingChange}
                generateAnswersDealRegistry={generateAnswersDealRegistry}
                dueDiligenceRecord={data?.getDueDiligenceRecord}
              />
            )} */}
            {editFieldPageType !== 'default' && editFieldPageType !== 'advanced-options' && (
              <FieldEditorModalPage
                allFields={allFields}
                currentField={currentField}
                ids={{
                  funnelId: dueDiligenceRecordData?.getDueDiligenceRecord?.funnel?.id ?? null,
                  funnelStaticId: dueDiligenceRecordData?.getDueDiligenceRecord?.funnel?.funnelStaticId ?? null
                }}
                investmentId={dueDiligenceRecordData?.getDueDiligenceRecord.investment.id ?? null}
                editFieldPageType={editFieldPageType as keyof typeof AIFieldType}
                setIsAiPaneOpen={setIsAiAssistantOpen}
                setCurrentField={(field) => {
                  setCurrentField(field);
                  handleChangeFieldId(field?.id || null);
                  handleChangeEditFieldPageType(field?.documentFieldType || null);
                }}
                setEditFieldPageType={handleChangeEditFieldPageType}
                mainQueryRefetch={dueDiligenceRecordRefetch}
                handleUpdateFieldAnswer={handleUpdateFieldAnswer}
                handleDeleteAttachment={handleDeleteAttachment}
                handleUploadAttachment={handleUploadAttachment}
                handleClose={closeEditPage}
              />
            )}
            {editFieldPageType === 'default' && (
              <>
                {isMobile && isNotesOpen && (
                  <MobileModal onClose={() => setIsNotesOpen(false)} title="Notes" onClick={() => null}>
                    <MobileNotes data={noteData} />
                  </MobileModal>
                )}
                <FieldModal
                  modalWindow={modalWindow}
                  currentRow={currentRow}
                  currentField={currentField}
                  recordId={params?.recordId}
                  activeTab={activeTab}
                  owner={dueDiligenceRecordData?.getDueDiligenceRecord.owner}
                  strategyId={dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.id}
                  updateTierStatus={updateTierStatus}
                  advanceToNexTier={advanceToNexTier}
                  backToPreviousTier={backToPreviousTier}
                  openEditPage={onEditField}
                  dueDiligenceRecordRefetch={dueDiligenceRecordRefetch}
                  closeModalWindow={closeModalWindow}
                />
                <MainWrap>
                  <Header
                    aiButtonConfig={{
                      isLoading: generateAnswersLoading,
                      secondaryIcon: isGenerateAnswersLoadingCompleted && <ApprovedIcon width={20} height={20} fill={themeVar('context.light')} />,
                      onClick: () => setIsAiAssistantOpen(!isAiAssistantOpen)
                    }}
                    modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/dueDiligence' })} backToTitle={backToTitle} />}
                  />
                  <PageTitle title={recordName} />
                  {recordStatus === DueDiligenceRecordStatus.Approved && (
                    <Banner
                      title={`Approved By ${dueDiligenceRecordData?.getDueDiligenceRecord.approver?.firstName} ${dueDiligenceRecordData
                        ?.getDueDiligenceRecord.approver?.lastName} on ${format(
                        new Date(dueDiligenceRecordData?.getDueDiligenceRecord.statusUpdatedAt),
                        'LLL d, yyyy'
                      )} `}
                      description={dueDiligenceRecordData?.getDueDiligenceRecord.note}
                      icon={<ApprovedIcon fill={themeVar('context.light')} />}
                      bgColor={themeVar('context.success')}
                      color={themeVar('context.light')}
                    />
                  )}
                  {recordStatus === DueDiligenceRecordStatus.Rejected && (
                    <Banner
                      title={`Rejected By ${dueDiligenceRecordData?.getDueDiligenceRecord.approver?.firstName} ${dueDiligenceRecordData
                        ?.getDueDiligenceRecord.approver?.lastName} on ${format(
                        new Date(dueDiligenceRecordData?.getDueDiligenceRecord.statusUpdatedAt),
                        'LLL d, yyyy'
                      )} `}
                      description={dueDiligenceRecordData?.getDueDiligenceRecord.note}
                      icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                      bgColor={themeVar('context.error')}
                      color={themeVar('context.light')}
                    />
                  )}
                  <PaddingWrap>
                    <Notes onClick={() => setIsNotesOpen(false)} isOpen={isNotesOpen} data={noteData} />
                    <TabsWrapper isMobile={isMobile}>
                      {showTabs ? (
                        <Tabs isMobile={isMobile} ref={tabsContainerRef}>
                          {tiers.map((item, index) => (
                            <TabsTitle key={item.id} onClick={() => setActiveTab(item.name)} activeTab={activeTab === item.name}>
                              {item.name}
                            </TabsTitle>
                          ))}
                        </Tabs>
                      ) : (
                        <BurgerMenuWrapper isMobile={isMobile} ref={burgerMenuRef}>
                          <ActiveTabWrapper>
                            {isTabMenuOpen ? (
                              <CloseMenuIcon onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
                            ) : (
                              <BurgerMenuIcon onClick={() => setIsTabMenuOpen(!isTabMenuOpen)} style={{ cursor: 'pointer' }} />
                            )}
                            <ActiveTab activeTab>{activeTab}</ActiveTab>
                          </ActiveTabWrapper>
                          {isTabMenuOpen && (
                            <BurgerMenu isMobile={isMobile}>
                              {tiers.map((item) => (
                                <MenuItem key={item.id} onClick={() => changeActiveFunnel(item.name)} activeTab={activeTab === item.name}>
                                  {item.name}
                                </MenuItem>
                              ))}
                            </BurgerMenu>
                          )}
                        </BurgerMenuWrapper>
                      )}
                    </TabsWrapper>
                    <TabsLine />
                    {tiers.map((tier) => (
                      <TierWrapper key={tier.id}>
                        {tier.name === activeTab && (
                          <>
                            <TierStatusWrapper isMobile={isMobile}>
                              <TierStatus
                                tierData={tier}
                                activeTab={activeTab}
                                firstTierName={tiers[0].name}
                                openModalWindow={openModalWindow}
                                updateRecordStatus={updateRecordStatus}
                                updateTierStatus={updateTierStatus}
                                recordId={params?.recordId}
                                isAllDone={!isFieldValue()}
                                isLastTier={isLastTier()}
                                recordStatus={recordStatus}
                                loading={isBackToLoading}
                              />
                              {tier.sections.filter((section) => section.userCanScore).length ? (
                                <TotalScore>
                                  Total Score
                                  {totalScore() === 0 && (
                                    <CountWrap>
                                      <Count>{totalScore()}</Count>
                                    </CountWrap>
                                  )}
                                  {totalScore() > 0 && (
                                    <CountWrap color={themeVar('context.success')}>
                                      <Count>+{totalScore()}</Count>
                                    </CountWrap>
                                  )}
                                  {totalScore() < 0 && (
                                    <CountWrap color={themeVar('context.error')}>
                                      <Count>{totalScore()}</Count>
                                    </CountWrap>
                                  )}
                                </TotalScore>
                              ) : (
                                <></>
                              )}
                            </TierStatusWrapper>
                            <TierDropdownWrapper>
                              {tier.sections.map((section) => (
                                <TierDropdown
                                  key={section.id}
                                  title={section.name}
                                  openNotes={openNotes}
                                  openModalWindow={openModalWindow}
                                  sectionData={section}
                                  tierStatus={tier.data.status}
                                  owner={dueDiligenceRecordData?.getDueDiligenceRecord.owner}
                                  prevTierStatus={prevTierStatus()}
                                  isBackToLoading={isBackToLoading}
                                  recordStatus={recordStatus}
                                >
                                  <FieldList
                                    fields={convertDueDiligenceFieldToFieldEditor(
                                      section?.fields || [],
                                      dueDiligenceRecordData?.getDueDiligenceRecord?.id
                                    )}
                                    mode="edit"
                                    isDisabled={
                                      section.data.completed ||
                                      prevTierStatus() === DueDiligenceRecordTierStatus.Rejected ||
                                      recordStatus === DueDiligenceRecordStatus.Approved ||
                                      recordStatus === DueDiligenceRecordStatus.Rejected
                                    }
                                    onEditField={(field) => onEditField(field.id, field.documentFieldType)}
                                    onAcceptAiAnswer={onAcceptAiAnswer}
                                    onClearField={onClearField}
                                    onOverrideAnswer={(field) => openModalWindow('override-answer', null, field)}
                                    onRelinkField={(field) => openModalWindow('relink-field', null, field)}
                                  />
                                </TierDropdown>
                              ))}
                              {tier.checklistItems.length ? (
                                <TierDropdown title={`${activeTab}: Checklist`} checkListData={tier.checklistItems}>
                                  <TierCheckList
                                    recordStatus={recordStatus}
                                    tierStatus={tier.data.status}
                                    checkListData={tier.checklistItems}
                                    openModalWindow={openModalWindow}
                                    openNotes={openNotes}
                                    owner={dueDiligenceRecordData?.getDueDiligenceRecord.owner}
                                    prevTierStatus={prevTierStatus()}
                                    isBackToLoading={isBackToLoading}
                                  />
                                </TierDropdown>
                              ) : (
                                <></>
                              )}
                            </TierDropdownWrapper>
                            <ButtonWrapper>
                              <Button onClick={() => setModalWindow({ isOpen: true, type: 'download-pdf' })} styleType="outline" size="lg">
                                Download
                              </Button>
                              {tier.data.status === DueDiligenceRecordTierStatus.InProgress && (
                                <>
                                  <Button
                                    onClick={() => openModalWindow('reject-investment', tier)}
                                    styleType={isFieldValue() ? 'disabled' : 'outline'}
                                    size="lg"
                                  >
                                    Reject
                                  </Button>
                                  {isLastTier() ? (
                                    <Button
                                      onClick={() => openModalWindow('approve-diligence', tier)}
                                      styleType={isFieldValue() ? 'disabled' : 'default'}
                                      size="lg"
                                    >
                                      Approve Diligence
                                    </Button>
                                  ) : (
                                    <Button
                                      onClick={() => openModalWindow('advance-to', tier)}
                                      styleType={isFieldValue() ? 'disabled' : 'default'}
                                      size="lg"
                                    >
                                      Advance To Next Tier
                                    </Button>
                                  )}
                                </>
                              )}
                            </ButtonWrapper>
                          </>
                        )}
                      </TierWrapper>
                    ))}
                  </PaddingWrap>
                </MainWrap>
              </>
            )}
          </>
        )}
        <AiAssistant
          isAiAssistantOpen={isAiAssistantOpen}
          ids={{
            recordId: dueDiligenceRecordData?.getDueDiligenceRecord?.id,
            investmentId: dueDiligenceRecordData?.getDueDiligenceRecord?.investment?.id
          }}
          type="ddr"
          filesForAiAssistant={filesForAiAssistant}
          tabs={editFieldPageType !== 'default' && editFieldPageType !== 'advanced-options' ? aiPaneTabsOnEditFieldPages : []}
          citationsTabData={citationsTabData}
          handleFilesForAiAssistant={handleFilesForAiAssistant}
          handleGenerateAnswersLoadingChange={handleGenerateAnswersLoadingChange}
          mainQueryRefetch={dueDiligenceRecordRefetch}
          openAdvancedOptions={openAdvancedOptions}
          onClose={() => setIsAiAssistantOpen(false)}
        />
      </DueDiligenceFunnelWrapper>
    </>
  );
};

const CustomLoader = styled(Loader)`
  height: 100%;
  align-items: center;
`;

const TabsWrapper = styled(PaddingWrap)<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  margin: 10px -24px 0 -24px;
`;

const Tabs = styled.div<{ isMobile: boolean }>`
  padding-top: 10px;
  display: flex;
  justify-content: ${({ isMobile }) => (isMobile ? 'center' : 'flex-start')};
  gap: 5px;
  order: ${({ isMobile }) => (isMobile ? 2 : 1)};
`;

const TabsLine = styled.div`
  border-bottom: 1px ${themeVar('border.base')} solid;
  margin-left: -50px;
  margin-right: -50px;
`;

const BurgerMenuWrapper = styled.div<{ isMobile: boolean }>`
  margin-top: 15px;
  position: relative;
  width: ${({ isMobile }) => (isMobile ? 'auto' : '70%')};
  order: ${({ isMobile }) => (isMobile ? 2 : 1)};
`;

const ActiveTabWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;
`;

const ActiveTab = styled(TabsTitle)`
  width: max-content;
  margin: 0;
  cursor: auto;
`;

const BurgerMenu = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: absolute;
  left: ${({ isMobile }) => (isMobile ? '-60px' : '0')};
  top: 60px;
  z-index: 10;
  background: ${themeVar('layer.1')};
  min-width: 300px;
  width: ${({ isMobile }) => (isMobile ? '100%' : 'auto')};
`;

const MenuItem = styled(TabsTitle)`
  margin: 0;
  border-radius: 0;
  padding: 10px 20px;
`;

const TierWrapper = styled.div``;

const TierStatusWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: ${({ isMobile }) => (isMobile ? 'start' : 'center')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '0')};
  padding: ${({ isMobile }) => (isMobile ? '30px 23.5px 20px 23.5px' : '30px 129px 20px 23.5px')};
`;

const TotalScore = styled.div`
  display: flex;
  gap: 10px;
  font-weight: 600;
  font-size: 13px;
  line-height: 18px;
  color: ${themeVar('font.weak')};
`;

const CountWrap = styled.div<{ color?: string }>`
  background-color: ${({ color }) => (color ? color : themeVar('layer.1'))};
  display: inline-block;
  border-radius: 12px;
  color: ${({ color }) => (color ? themeVar('context.light') : themeVar('font.strong'))};
  padding: 1px 11px;
  font-size: 13px;
  margin-right: 6px;
`;

const Count = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
`;

const TierDropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 30px 0 20px 0;
`;

const DueDiligenceFunnelWrapper = styled.div`
  display: flex;
  justify-content: center;
  transition: all 0.3s;
`;
